import { BarChart, ChevronRight } from '@mui/icons-material';
import { Box, Card, CardContent, CardHeader, Grid, Tooltip, Typography } from '@mui/material';
import { useAppDispatch } from '@src/app/hooks/store';
import { selectOnePortfolioByKey } from '@src/app/store/reducers/portfolios';
import { numberFormatterDollar, percentageWithSign, positiveNegativeColor } from '@src/app/utils/GenericUtils';
import { holdingsUrl } from '@src/app/utils/UrlBuilder';
import { FC, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

interface PerformanceCardProps {
  name: string;
  totalValue: number;
  capitalReturnValue: number;
  capitalReturnPercentage: number;
  incomeReturnValue: number;
  incomeReturnPercentage: number;
  totalReturnPercentage: number;
  totalReturnValue: number;
  portfolioKey: string;
}

interface PerformanceCardReturnProps {
  type: string;
  returnValue: number;
  returnPercent: number;
}

const PerformanceCardReturnColumn: FC<PerformanceCardReturnProps> = ({
  returnValue,
  returnPercent,
  type,
}: PerformanceCardReturnProps): JSX.Element => {
  return (
    <Grid item container spacing={1}>
      <Grid item xs={12} sm={8}>
        <Typography fontWeight={600} color="#222222" fontSize={14}>
          {type}
        </Typography>
      </Grid>
      <Grid item container spacing={1} xs={12} sm={4}>
        <Grid item>
          <Typography sx={{ color: positiveNegativeColor(returnValue) }} fontWeight={700} fontSize={14}>
            {numberFormatterDollar(returnValue)}
          </Typography>
        </Grid>
        <Grid item>
          <Typography sx={{ color: positiveNegativeColor(returnPercent) }} fontWeight={700} fontSize={14}>
            {`(${percentageWithSign(returnPercent)})`}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const PerformanceCard: FC<PerformanceCardProps> = ({
  totalValue,
  capitalReturnValue,
  capitalReturnPercentage,
  incomeReturnValue,
  incomeReturnPercentage,
  totalReturnPercentage,
  totalReturnValue,
  portfolioKey,
  name,
}: PerformanceCardProps): JSX.Element => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  const dispatch = useAppDispatch();
  const history = useHistory();

  const selectPortfolio = () => {
    dispatch(selectOnePortfolioByKey(portfolioKey));
  };

  const handleNavigation = () => {
    selectPortfolio();
    history.push(holdingsUrl());
  };

  useEffect(() => {
    if (cardRef.current) {
      setWidth(cardRef.current.offsetWidth);
    }
  }, [cardRef.current?.offsetWidth]);

  return (
    <Tooltip title={name}>
      <Card
        onClick={handleNavigation}
        ref={cardRef}
        elevation={0}
        sx={{
          overflow: 'hidden',
          ':hover': {
            cursor: 'pointer',
          },
          paddingTop: '20px',
          paddingRight: '16px',
          paddingLeft: '16px',
          paddingBottom: '20px',
          borderRadius: '24px',
          borderWidth: '1px',
          border: '1px solid #E9E9E9',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
        }}
      >
        <CardHeader
          sx={{
            overflow: 'hidden',
          }}
          title={
            <Box display="flex" alignItems="center" sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}>
              <BarChart sx={{ color: '#222222', flexShrink: 0, mr: 1 }} />
              <Typography
                fontSize={16}
                color="#222222"
                noWrap
                sx={{
                  maxWidth: width ? `${Math.max(width - 120, 0)}px` : '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                {name}
              </Typography>
            </Box>
          }
          action={<ChevronRight sx={{ color: '#222222', flexShrink: 0 }} />}
        />
        <CardContent>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <Typography fontWeight={600} fontSize={28}>
                {numberFormatterDollar(totalValue)}
              </Typography>
            </Grid>
            <Grid item container direction="column" spacing={1}>
              <PerformanceCardReturnColumn type="Capital Return" returnValue={capitalReturnValue || 0} returnPercent={capitalReturnPercentage || 0} />
              <PerformanceCardReturnColumn type="Income Return" returnValue={incomeReturnValue || 0} returnPercent={incomeReturnPercentage || 0} />
              <PerformanceCardReturnColumn type="Total Return" returnValue={totalReturnValue || 0} returnPercent={totalReturnPercentage || 0} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Tooltip>
  );
};
